import * as React from 'react'
import { useState, useEffect } from 'react'
import {
    useDataProvider,
    useNotify,
    Button,
    Loading,
    FunctionField,
    DateField
} from 'react-admin'
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Stack,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Collapse
} from '@mui/material'
import { UserConsent, User } from '@/types'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import HowToRegIcon from '@mui/icons-material/HowToReg'
import BlockIcon from '@mui/icons-material/Block'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

interface UserConsentListProps {
    userId: number
    user?: User
}

export default function UserConsentList({ userId, user }: UserConsentListProps) {
    const [consents, setConsents] = useState<UserConsent[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [expandedConsent, setExpandedConsent] = useState<number | null>(null)
    const dataProvider = useDataProvider()
    const notify = useNotify()

    const fetchConsents = async () => {
        try {
            setLoading(true)
            const result = await dataProvider.getList(`users/${userId}/consents`, {
                pagination: { page: 1, perPage: 50 },
                sort: { field: 'createdAt', order: 'DESC' },
                filter: {}
            })
            setConsents(result.data)
            setError(null)
        } catch (err) {
            setError('Erreur lors du chargement des consentements')
            console.error('Error fetching consents:', err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchConsents()
    }, [userId])

    const handleGrantConsent = async (purposeCode: string) => {
        try {
            await dataProvider.create(`users/${userId}/consents/grant`, {
                data: { purpose_code: purposeCode }
            })
            notify('Consentement accordé avec succès', { type: 'success' })
            fetchConsents()
        } catch (error: any) {
            notify(error?.body?.error || 'Erreur lors de l\'octroi du consentement', { type: 'error' })
        }
    }

    const handleRevokeConsent = async (purposeCode: string) => {
        try {
            await dataProvider.create(`users/${userId}/consents/revoke`, {
                data: { purpose_code: purposeCode }
            })
            notify('Consentement révoqué avec succès', { type: 'success' })
            fetchConsents()
        } catch (error: any) {
            notify(error?.body?.error || 'Erreur lors de la révocation du consentement', { type: 'error' })
        }
    }

    const toggleExpand = (consentId: number) => {
        setExpandedConsent(expandedConsent === consentId ? null : consentId)
    }

    const handleCopyHash = async (hash: string) => {
        try {
            await navigator.clipboard.writeText(hash)
            notify('Hash copié dans le presse-papiers', { type: 'success' })
        } catch (error) {
            notify('Erreur lors de la copie du hash', { type: 'error' })
        }
    }

    if (loading) return <Loading />

    if (error) {
        return (
            <Alert severity="error">
                {error}
            </Alert>
        )
    }

    const activeConsents = consents.filter(c => c.isGranted && !c.revokedAt)
    const revokedConsents = consents.filter(c => !c.isGranted || c.revokedAt)

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Consentements RGPD {user ? `de ${user.firstName} ${user.lastName}` : ''}
            </Typography>

            {/* Statistiques */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Card sx={{ minWidth: 120 }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography color="success.main" variant="h4">
                            {activeConsents.length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Consentements actifs
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ minWidth: 120 }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography color="error.main" variant="h4">
                            {revokedConsents.length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Consentements révoqués
                        </Typography>
                    </CardContent>
                </Card>
            </Stack>

            {/* Consentements actifs */}
            {activeConsents.length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="success.main">
                            <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Consentements actifs
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Usage</TableCell>
                                        <TableCell>Version</TableCell>
                                        <TableCell>Hash</TableCell>
                                        <TableCell>Date du consentement</TableCell>
                                        <TableCell>Actions</TableCell>
                                        <TableCell width="48px"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activeConsents.map((consent) => (
                                        <React.Fragment key={consent.id}>
                                            <TableRow>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {consent.consentPurpose.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {consent.consentPurpose.code}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        size="small" 
                                                        label={consent.consentPurpose.version}
                                                        color={consent.consentPurpose.versionType === 'MAJOR' ? 'error' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '10px' }}>
                                                            {consent.hashed_email || consent.hashedEmail ? 
                                                                (consent.hashed_email || consent.hashedEmail).substring(0, 8) + '...' 
                                                                : 'N/A'}
                                                        </Typography>
                                                        {(consent.hashed_email || consent.hashedEmail) && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleCopyHash(consent.hashed_email || consent.hashedEmail)}
                                                                title="Copier le hash complet"
                                                                sx={{ p: 0.25 }}
                                                            >
                                                                <ContentCopyIcon sx={{ fontSize: 12 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {consent.consentedAt ? new Date(consent.consentedAt).toLocaleString() : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        label="Révoquer"
                                                        onClick={() => handleRevokeConsent(consent.consentPurpose.code)}
                                                        size="small"
                                                        color="error"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleExpand(consent.id)}
                                                    >
                                                        {expandedConsent === consent.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ py: 0 }}>
                                                    <Collapse in={expandedConsent === consent.id}>
                                                        <Box sx={{ py: 2, backgroundColor: 'action.hover' }}>
                                                            <Typography variant="body2" paragraph>
                                                                <strong>Détails du consentement :</strong>
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                                <strong>Version :</strong> v{consent.consentPurpose.version} ({consent.consentPurpose.versionType})
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mb: 2 }}>
                                                                <strong>Statut de version :</strong> {consent.consentPurpose.isActive ? 'Active' : 'Inactive'}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                User Agent: {consent.userAgent || 'Non disponible'}
                                                            </Typography>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* Consentements révoqués */}
            {revokedConsents.length > 0 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="error.main">
                            <CancelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Consentements révoqués
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Usage</TableCell>
                                        <TableCell>Version</TableCell>
                                        <TableCell>Hash</TableCell>
                                        <TableCell>Date de révocation</TableCell>
                                        <TableCell>Actions</TableCell>
                                        <TableCell width="48px"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {revokedConsents.map((consent) => (
                                        <React.Fragment key={consent.id}>
                                            <TableRow>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {consent.consentPurpose.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {consent.consentPurpose.code}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        size="small" 
                                                        label={consent.consentPurpose.version}
                                                        color={consent.consentPurpose.versionType === 'MAJOR' ? 'error' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '10px' }}>
                                                            {consent.hashed_email || consent.hashedEmail ? 
                                                                (consent.hashed_email || consent.hashedEmail).substring(0, 8) + '...' 
                                                                : 'N/A'}
                                                        </Typography>
                                                        {(consent.hashed_email || consent.hashedEmail) && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleCopyHash(consent.hashed_email || consent.hashedEmail)}
                                                                title="Copier le hash complet"
                                                                sx={{ p: 0.25 }}
                                                            >
                                                                <ContentCopyIcon sx={{ fontSize: 12 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {consent.revokedAt ? new Date(consent.revokedAt).toLocaleString() : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        label="Accorder"
                                                        onClick={() => handleGrantConsent(consent.consentPurpose.code)}
                                                        size="small"
                                                        color="success"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleExpand(consent.id)}
                                                    >
                                                        {expandedConsent === consent.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ py: 0 }}>
                                                    <Collapse in={expandedConsent === consent.id}>
                                                        <Box sx={{ py: 2, backgroundColor: 'action.hover' }}>
                                                            <Typography variant="body2" paragraph>
                                                                <strong>Détails du consentement :</strong>
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                                <strong>Version :</strong> v{consent.consentPurpose.version} ({consent.consentPurpose.versionType})
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mb: 2 }}>
                                                                <strong>Statut de version :</strong> {consent.consentPurpose.isActive ? 'Active' : 'Inactive'}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                User Agent: {consent.userAgent || 'Non disponible'}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                Accordé le: {consent.consentedAt ? new Date(consent.consentedAt).toLocaleString() : 'Non disponible'}
                                                            </Typography>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {consents.length === 0 && (
                <Alert severity="info">
                    Aucun consentement trouvé pour cet utilisateur.
                </Alert>
            )}
        </Box>
    )
}